import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { Subscription } from 'rxjs';
import { AttritionData, ClaimsData, EmploymentType, GroupBy, LeaveData, OverviewData } from '../workforce.types';
import { WorkforceApiService } from '../workforce-api.service';
import { WorkforceFilters } from '../filters/filters.component';
import { ATTRITION_SERIES, CLAIMS_SERIES, reportingPeriod } from '../report-presentation';

interface ReportState<T> { data: T | null; loading: boolean; error: string; }
function emptyReport<T>(): ReportState<T> { return { data: null, loading: false, error: '' }; }
type Section = 'summary' | 'attrition' | 'claims' | 'leave';

@Component({ selector: 'app-overview', standalone: false, templateUrl: './overview.component.html' })
export class OverviewComponent implements OnInit, OnDestroy {
  private readonly api = inject(WorkforceApiService);
  private subscription = new Subscription();
  private requestNumber = 0;
  readonly summary = emptyReport<OverviewData>();
  readonly attrition = emptyReport<AttritionData>();
  readonly claims = emptyReport<ClaimsData>();
  readonly leave = emptyReport<LeaveData>();
  readonly attritionSeries = ATTRITION_SERIES;
  readonly claimsSeries = CLAIMS_SERIES;
  get data(): OverviewData | null { return this.summary.data; }
  get error(): string { return this.summary.error; }
  get loading(): boolean { return this.summary.loading; }
  groupBy: GroupBy = 'department';
  startMonth = '';
  endMonth = '';
  employmentType: EmploymentType = '';

  get groupLabel(): string { return { department: 'Department', business: 'Business', company: 'Company' }[this.groupBy]; }
  get claimsApplicable(): boolean { return this.employmentType !== 'Off-Roll'; }
  get invalidRange(): boolean { return Boolean(this.startMonth && this.endMonth && this.startMonth > this.endMonth); }
  get periodCaption(): string { return reportingPeriod(this.startMonth, this.endMonth, 18); }
  get leavePeriodCaption(): string {
    const months = this.leave.data?.months;
    return months?.length ? reportingPeriod(months[0], months[months.length - 1], 12) : reportingPeriod(this.startMonth, this.endMonth, 12);
  }

  ngOnInit(): void { this.load(); }
  ngOnDestroy(): void { this.requestNumber++; this.subscription.unsubscribe(); }
  load(force = false): void {
    this.requestNumber++;
    this.subscription.unsubscribe();
    this.subscription = new Subscription();
    [this.summary, this.attrition, this.claims, this.leave].forEach(section => {
      section.data = null;
      section.error = '';
      section.loading = false;
    });
    if (this.invalidRange) return;
    for (const section of ['summary', 'attrition', 'claims', 'leave'] as Section[]) this.loadSection(section, force);
  }

  retrySection(section: Section): void { this.loadSection(section, true); }

  private loadSection(section: Section, force: boolean): void {
    if (this.invalidRange || (section === 'claims' && !this.claimsApplicable)) return;
    const params = {
      groupBy: this.groupBy, startMonth: this.startMonth || undefined,
      endMonth: this.endMonth || undefined,
      employmentType: section === 'claims' ? 'On-Roll' : this.employmentType || undefined,
    };
    switch (section) {
      case 'summary': this.fetchReport(this.summary, '/workforce/summary', params, force); break;
      case 'attrition': this.fetchReport(this.attrition, '/workforce/attrition-trends', params, force); break;
      case 'claims': this.fetchReport(this.claims, '/workforce/expense-summary', params, force); break;
      case 'leave': this.fetchReport(this.leave, '/workforce/leave-map', params, force); break;
    }
  }

  private fetchReport<T>(section: ReportState<T>, path: string, params: Record<string, string | undefined>, force: boolean): void {
    const requestNumber = this.requestNumber;
    section.loading = true;
    section.error = '';
    section.data = null;
    this.subscription.add(this.api.get<T>(path, params, force).subscribe({
      next: data => {
        if (requestNumber !== this.requestNumber) return;
        section.data = data;
        section.loading = false;
      },
      error: () => {
        if (requestNumber !== this.requestNumber) return;
        section.error = 'Unable to load this section. Please retry.';
        section.loading = false;
      },
    }));
  }
  applyFilters(filters: WorkforceFilters): void {
    this.groupBy = filters.groupBy;
    this.startMonth = filters.startMonth;
    this.endMonth = filters.endMonth;
    this.employmentType = filters.employmentType;
    this.load();
  }
  clearFilters(): void { this.groupBy = 'department'; this.startMonth = ''; this.endMonth = ''; this.employmentType = ''; this.load(); }
}
